import { useDispatch, useSelector } from 'react-redux'
import type { TypedUseSelectorHook } from 'react-redux'

import { configureStore } from '@reduxjs/toolkit'
import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit'
import { combineReducers } from 'redux'
import type { Reducer, StateFromReducersMapObject } from 'redux'

import { env } from '~publish/env'

import { getMiddleware } from './middlewares'
import { reducers as partialReducers } from './reducers'

const reducers = {
  ...partialReducers,
}
export type RootState = StateFromReducersMapObject<typeof reducers>
type AsyncReducerMap = Record<string, Reducer>

const isTest = env.VITE_NODE_ENV === 'test'
const isProd = env.VITE_NODE_ENV === 'production'

const defualtMiddlewareOptions = {
  serializableCheck: isTest
    ? false
    : {
        ignoredActionPaths: ['queryParams'],
        ignoredActions: [],
      },
}

// Setup store with basic root reducer for type safety
export const store = configureStore({
  reducer: combineReducers(reducers),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware(defualtMiddlewareOptions).concat(getMiddleware()),
  devTools: !isProd,
})

// This method combines all our reducers together
// to allow asynchronously adding more reducers
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export const createReducer = (asyncReducers: AsyncReducerMap = {}) =>
  combineReducers({
    ...reducers,
    ...asyncReducers,
  })

let asyncReducers: AsyncReducerMap = {}

// This is how lazy-loaded packages can add their stores once loaded
export const injectReducers = function (reducerMap: AsyncReducerMap): void {
  asyncReducers = {
    ...asyncReducers,
    ...reducerMap,
  }
  // redux 5's combineReducers infers a structurally distinct (though
  // equivalent) preloaded-state generic for this second combineReducers call
  // versus the one that built `store` above, so TS sees two unrelated
  // Reducer types here even though both describe the same RootState shape.
  // @ts-expect-error TS(2345) FIXME: redux 5 combineReducers preloaded-state
  // generic identity mismatch between separately evaluated calls.
  store.replaceReducer(createReducer(asyncReducers))
}

// Typed against AnyAction rather than redux 5's stricter UnknownAction so the
// codebase's existing loosely-typed action objects keep compiling.
export type AppDispatch = ThunkDispatch<RootState, undefined, AnyAction>

// Widened dispatch for call sites outside of React (e.g. module-level
// helpers) that can't use the useAppDispatch hook below. Same underlying
// store.dispatch, just retyped.
export const dispatch: AppDispatch = store.dispatch

// Use throughout your app instead `useDispatch`  for better type inference
export const useAppDispatch = (): AppDispatch => useDispatch<AppDispatch>()

// Use throught your app instead of useDispatch for better type inference
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

export default store
